import {
  Modal,
  Text,
  TextLink,
  Shelf,
  Toggle,
  Stack,
} from "@chaser/components";
import { bool, func, string } from "prop-types";
import { useState, useMemo } from "react";
import cx from "classnames";
import useModuleInfo from "../../hooks/useModuleInfo";
import { useFeatureContext } from "./featureContext";
import style from "./UpgradeModal.module.scss";
import emitter from "../../util/emitter";
import Plans from "../../app/settings/components/Subscriptions/SubscriptionOffers/Plans";
import { BillingPeriods, BillingProvider, Modules } from "../../util/constants";

function useAllowedPlans(plans, contextPlans) {
  return useMemo(() => {
    if (!contextPlans || !plans.length) {
      return {
        allowedPlans: plans,
        allowedPlansIncludingCurrent: plans,
      };
    }

    const contextPlansSet = new Set(contextPlans);

    return {
      allowedPlans: plans.filter((p) => contextPlansSet.has(p.name)),
      allowedPlansIncludingCurrent: plans.filter(
        (p) => p.isCurrent || contextPlansSet.has(p.name),
      ),
    };
  }, [plans, contextPlans]);
}

const UpgradeModal = ({
  open,
  close,
  onClosed,
  headerText = "Upgrade your plan to access this feature.",
  moduleName = Modules.RECEIVABLES,
}) => {
  const info = useModuleInfo(moduleName);
  const ctx = useFeatureContext();

  const subscriptionService =
    info.module?.subscriptionService || BillingProvider.CHARGEBEE;
  const hideAnnualOffers = [BillingProvider.XERO].includes(subscriptionService);
  const plan = info.module?.plan;

  const toggled =
    (hideAnnualOffers || plan?.period === BillingPeriods.MONTHLY) ?? false;
  const [isToggled, handleToggle] = useState(toggled);

  const plans = info.plans || [];
  const contextPlans = ctx?.plans;

  const { allowedPlans, allowedPlansIncludingCurrent } = useAllowedPlans(
    plans,
    contextPlans,
  );

  return (
    <Modal
      open={open}
      toggle={close}
      onClosed={onClosed}
      size="large"
      title="Upgrade your plan"
      className={cx(
        style.modal,
        style[`size-${allowedPlansIncludingCurrent.length}`],
      )}
    >
      <Modal.Body>
        <Stack gap="large">
          {headerText && <Text fontSize="medium">{headerText}</Text>}
          {!hideAnnualOffers && (
            <Shelf justifyContent="center">
              <Text>Yearly (save 10%)</Text>
              <Toggle toggleOn={isToggled} handleToggle={handleToggle} />
              <Text>Monthly</Text>
            </Shelf>
          )}
        </Stack>
        {allowedPlans.length ? (
          <Plans
            appearance="secondary"
            plans={allowedPlansIncludingCurrent}
            onSubscribeSuccess={() => close()}
            refetchSubscriptionData={info.refetchSubscriptionData}
            period={!isToggled ? BillingPeriods.YEARLY : BillingPeriods.MONTHLY}
          />
        ) : (
          <>
            <Text>
              No plans available, please contact{" "}
              <TextLink
                as="button"
                onClick={() => {
                  close();
                  emitter.emit("openSupportModal");
                }}
              >
                support
              </TextLink>
            </Text>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

UpgradeModal.propTypes = {
  open: bool,
  close: func,
  onClosed: func,
  headerText: string,
  moduleName: string,
};

export default UpgradeModal;
